import { useContext } from 'react';
import * as React from 'react';
import { createFragmentContainer, graphql } from 'react-relay';
import classnames from 'classnames';

// Components
import { Net } from './Net';
import { Sale } from './Sale';
import { Retail } from './Retail';

// Helpers
import { textPriceValue, types, isValidTextPriceType } from '../helpers/priceTypeConstants';
import { TileContext } from '../helpers/TileContext';
import { FontSizeType } from '../types/StyleSizes';

import { Price_item as ItemType } from './__generated__/Price_item.graphql';

// Styles
import styles from './styles.scss';

type PriceProps = {
    currency: string;
    item: ItemType | null;
    hidePercentageOff?: boolean;
    priceBold?: boolean;
    isSbTile?: boolean;
    priceFontSize?: FontSizeType;
};

export const PriceComponent: React.FC<PriceProps> = ({
    currency,
    item,
    hidePercentageOff,
    priceBold,
    isSbTile,
    priceFontSize,
}) => {
    const { imageSize = 'imageMedium', fontSize = 'fontSmall' } = useContext(TileContext);

    const displayPrice = item?.displayPrice?.[0];
    // Price display for SB page type always returns a single price type
    const {
        textType,
        amountType,
        convertedAmountList,
        quantityDisplay,
        percentageOff,
        showPriceLabel,
        showPriceVariability,
    } = displayPrice || {};
    const percentageValue = hidePercentageOff ? null : percentageOff;

    const amountByCurrency = (convertedAmountList || []).find(
        convertedAmount => convertedAmount?.currency === currency
    );

    const price = Math.floor(amountByCurrency?.amount || 0) || 0;

    const sharedProps = {
        currency,
        price,
        quantityDisplay,
        showPriceVariability,
        isSbTile,
    };

    const isValidTextPrice = isValidTextPriceType(textType);

    const textTypePriceClass = classnames(
        styles.price,
        {
            [styles.sold]: textType === types.SOLD,
            [styles.hold]: textType === types.HOLD,
            [styles.isSbTile]: isSbTile,
        },
        styles[imageSize]
    );

    const priceClass = classnames(
        styles.price,
        styles[priceFontSize || fontSize],
        {
            [styles.isSbTile]: isSbTile,
            [styles.priceBold]: priceBold,
            [styles.priceLineThrough]: isValidTextPrice,
        },
        styles[imageSize]
    );

    if (isValidTextPrice) {
        const showPrice = price > 0 && textType !== types.PUR;
        return (
            <div data-tn={`price-${textType}`} className={styles.textTypePriceWrapper}>
                <div className={textTypePriceClass}>{textPriceValue(textType)}</div>
                {showPrice && (
                    <div className={priceClass} data-tn="price-retail">
                        <Retail {...sharedProps} />
                    </div>
                )}
            </div>
        );
    }

    if (!price || !amountType) {
        return null;
    }

    if (amountType === types.SALE) {
        return (
            <div className={priceClass} data-tn="price-sale">
                <Sale {...sharedProps} percentageOff={percentageValue} />
            </div>
        );
    } else if (amountType === types.NET) {
        return (
            <div className={priceClass} data-tn="price-net">
                <Net {...sharedProps} percentageOff={percentageValue} />
            </div>
        );
    } else if (amountType === types.RETAIL) {
        return (
            <div className={priceClass} data-tn="price-retail">
                <Retail {...sharedProps} showLabel={showPriceLabel} />
            </div>
        );
    }

    return null;
};

PriceComponent.defaultProps = {
    currency: 'USD',
    isSbTile: false,
};

export const Price = createFragmentContainer(PriceComponent, {
    item: graphql`
        fragment Price_item on Item
        @argumentDefinitions(
            isTrade: { type: "Boolean", defaultValue: false }
            pageDisplayEnum: { type: "PageDisplayEnum", defaultValue: searchAndBrowse }
            showPrice: { type: "Boolean", defaultValue: true }
        ) {
            displayPrice(page: $pageDisplayEnum, isTrade: $isTrade) @include(if: $showPrice) {
                convertedAmountList {
                    amount
                    currency
                }
                quantityDisplay
                percentageOff
                textType
                amountType
                showPriceLabel
                showPriceVariability
            }
        }
    `,
});
